/**
 * RMC Order Persistence Service
 * Handles conversion of persisted order data to form data and contact information initialization
 */

import {FullLoadOption} from '@hconnect/tuc-processes/fields/fullLoad/types'
import {FieldsForm} from '@hconnect/tuc-processes/fields/types'
import {TruckConfiguration} from '@hconnect/tuc-processes/fields/trucks/types'
import {getTruckSize, isMultipleTruck} from '@hconnect/tuc-processes/fields/trucks/trucks.utils'
import {PumpForm} from '@hconnect/tuc-processes/fields/unloadingMethod/types'

import {convertToMinutes} from '../../../../util/time'
import {parsePhoneNumber} from '../utils/phoneNumberUtils'

import {RmcOrderPayload} from './rmcOrderPlacementService'

interface PersistedOrderFormContext {
  quantityUom?: string
  fullLoadOptions?: FullLoadOption[]
  defaultFormData: FieldsForm
  truckConfiguration: TruckConfiguration['trucks']
}

/**
 * Helper fn to build material variations (slump and set control)
 */
const buildMaterialVariations = (payload: RmcOrderPayload) => {
  if (!payload.slump && !payload.setControl) {
    return {}
  }

  return {
    materialVariations: {
      slump: payload.slump ? {value: payload.slump, description: payload.slump} : null,
      setControl: payload.setControl
        ? {value: payload.setControl, description: payload.setControl}
        : null
    }
  }
}

/**
 * Reconstructs the QuantitySelection from persisted payload data.
 * For fullLoads orders, looks up the matching option to restore minQuantity/maxQuantity.
 */
const buildQuantityFromPayload = (
  payload: RmcOrderPayload,
  quantityUom: string,
  fullLoadOptions?: FullLoadOption[]
): FieldsForm['quantity'] => {
  if (
    payload.capacity.quantityType === 'FullLoads' &&
    fullLoadOptions &&
    fullLoadOptions.length > 0
  ) {
    const fullLoads = payload.capacity.quantity
    const matchingOption = fullLoadOptions.find((opt) => opt.numberOfLoads === fullLoads)
    if (matchingOption) {
      return {
        type: 'fullLoads',
        fullLoads,
        minQuantity: matchingOption.minQuantity,
        maxQuantity: matchingOption.maxQuantity,
        quantityUom
      }
    }
  }
  return {
    type: 'volume',
    volume: payload.capacity.quantity,
    quantityUom
  }
}

/**
 * Converts RmcOrderPayload (persisted order data) to FieldsForm for creating orders
 */
export const convertRmcOrderPayloadToFieldsForm = (
  payload: RmcOrderPayload,
  userCountry: string,
  context: PersistedOrderFormContext
): FieldsForm => {
  const {quantityUom = 'm³', fullLoadOptions, defaultFormData, truckConfiguration} = context
  const quantity = buildQuantityFromPayload(payload, quantityUom, fullLoadOptions)
  const multipleTruck =
    quantity.type === 'fullLoads'
      ? quantity.fullLoads >= 2
      : isMultipleTruck(quantity.volume, getTruckSize(truckConfiguration, payload.truckType))

  const baseFormData: FieldsForm = {
    ...defaultFormData,
    contract: {
      contractNumber: payload.contractNumber,
      contractItemPositionNumber: payload.contractItem
    },
    material: {
      materialId: payload.materialId,
      materialNumber: payload.materialNumber,
      materialDescription: payload.materialDescription,
      customerMaterialDescription: payload.customerMaterialDescription
    },
    quantity,
    plusLoadExpected: payload.capacity.quantityChangeExpected || false,
    isMultipleTruck: multipleTruck,
    maxLoadSize: payload.maxLoadSize,
    concreteUsage: payload.concreteUsage,
    unloadingMethod: {
      unloadingMethodCode: payload.unloadingMethod,
      // Restore pump configuration if it exists in the persisted data
      ...(payload.pumpConfiguration ? buildPumpForm(payload.pumpConfiguration) : {})
    },
    truckType: payload.truckType,
    dateTime: [payload.shippingDate],
    poNumber: payload.customerReference || '',
    additionalNotes: payload.orderCreateNotes || '',
    driverInstructions: payload.instructions || '',
    isConfirmedOrder: payload.isConfirmedByCustomer || false,
    orderPlacer: {
      name: payload.contactInformation?.orderPlacerName || '',
      phoneNumber: payload.contactInformation?.orderPlacerNumber
        ? parsePhoneNumber(payload.contactInformation.orderPlacerNumber, userCountry)
        : {dialingCode: '', number: '', country: userCountry}
    },
    siteContact: {
      name: payload.contactInformation?.siteContactName || '',
      phoneNumber: payload.contactInformation?.siteContactNumber
        ? parsePhoneNumber(payload.contactInformation.siteContactNumber, userCountry)
        : {dialingCode: '', number: '', country: userCountry}
    },
    ...buildMaterialVariations(payload)
  }

  return addIntervalConfiguration(baseFormData, payload)
}

/**
 * Helper to add interval configurations to form data
 */
const addIntervalConfiguration = (formData: FieldsForm, payload: RmcOrderPayload): FieldsForm => {
  // Add retarder if present
  if (payload.retarder) {
    formData.retarder = {
      retarder: payload.retarder,
      retarderUom: 'h' // todo: use value from package
    }
  }

  // Add isTestBooked if present (AU only)
  if (payload.isTestBooked !== undefined) {
    formData.isTestBooked = payload.isTestBooked
  }

  if (payload.timeOnJobSite) {
    const timeInMinutes = convertToMinutes(payload.timeOnJobSite)
    formData.timeOnJobSite = {
      time: timeInMinutes,
      timeUom: 'min'
    }
  }
  if (payload.loadSpace) {
    const timeParts = payload.loadSpace.split(':')
    const totalMinutes = parseInt(timeParts[0]) * 60 + parseInt(timeParts[1])
    formData.spacingOrFlowRate = {
      type: 'loadSpace' as const,
      loadSpace: totalMinutes,
      loadSpaceUom: 'min'
    }
  }
  if (payload.flowRate) {
    formData.spacingOrFlowRate = {
      type: 'flowRate' as const,
      flowRate: payload.flowRate,
      flowRateUom: 'm3/h'
    }
  }

  return formData
}

const buildPumpForm = (payload: NonNullable<RmcOrderPayload['pumpConfiguration']>): PumpForm => {
  return {
    pumpNumber: payload?.pumpNumber,
    pumpSettings: {
      canLargerPumpFit: payload.canLargerPumpFit,
      isPossibleToCleanPumpOnSite: payload.canLargerPumpFit,
      discharge:
        payload.discharge === 'hose'
          ? {
              method: payload.discharge,
              hoseLength: payload.hoseLength!
            }
          : {method: payload.discharge}
    }
  }
}

/**
 * Extracts contact information from persisted order data
 */
export const extractContactInfoFromPersistedOrder = (
  persistedOrder: RmcOrderPayload,
  userProfileCountry: string
) => {
  const contactInfo = persistedOrder.contactInformation

  if (!contactInfo) {
    return null
  }

  const orderPlacerContact =
    contactInfo.orderPlacerName || contactInfo.orderPlacerNumber
      ? {
          name: contactInfo.orderPlacerName || '',
          phoneNumber: contactInfo.orderPlacerNumber
            ? parsePhoneNumber(contactInfo.orderPlacerNumber, userProfileCountry)
            : {dialingCode: '', number: '', country: userProfileCountry}
        }
      : null

  const siteContact =
    contactInfo.siteContactName || contactInfo.siteContactNumber
      ? {
          name: contactInfo.siteContactName || '',
          phoneNumber: contactInfo.siteContactNumber
            ? parsePhoneNumber(contactInfo.siteContactNumber, userProfileCountry)
            : {dialingCode: '', number: '', country: userProfileCountry}
        }
      : null

  return {
    orderPlacer: orderPlacerContact,
    siteContact: siteContact
  }
}

/**
 * Creates a unique identifier for a set of persisted orders
 * Used to track which orders have been processed to prevent duplicates
 */
export const createPersistedOrdersId = (persistedOrders: RmcOrderPayload[]): string => {
  return persistedOrders
    .map((order) => `${order.materialNumber}-${order.capacity.quantity}-${order.shippingDate}`)
    .sort()
    .join('|')
}

/**
 * Processes persisted orders and converts them to form data
 */
export const processPersistedOrders = (
  persistedOrders: RmcOrderPayload[],
  userProfileCountry: string,
  context: PersistedOrderFormContext
): {
  formDataArray: FieldsForm[]
  contactInfo: ReturnType<typeof extractContactInfoFromPersistedOrder>
  persistedOrdersId: string
} => {
  if (!persistedOrders || persistedOrders.length === 0) {
    return {
      formDataArray: [],
      contactInfo: null,
      persistedOrdersId: ''
    }
  }

  // Convert each persisted order to form data
  const formDataArray = persistedOrders.map((persistedOrder) => {
    const formData = convertRmcOrderPayloadToFieldsForm(persistedOrder, userProfileCountry, context)
    return formData
  })

  // Extract contact information from the first order
  const contactInfo = extractContactInfoFromPersistedOrder(persistedOrders[0], userProfileCountry)

  // Create unique identifier for this set of orders
  const persistedOrdersId = createPersistedOrdersId(persistedOrders)

  return {
    formDataArray,
    contactInfo,
    persistedOrdersId
  }
}
